<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="8"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />

          <b-col
            md="2"
            class="mt-0"
          >
            <b-dropdown
              v-if="getUserPermission('EXPORT_EMAIL_REPORT')"
              id="dropdown-1"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              :text="$t('Export')"
              variant="primary"
              :loading="isLoading"
            >
              <b-dropdown-item @click="csvExport">
                {{ $t('CSV') }}
              </b-dropdown-item>
              <b-dropdown-item @click="excelExport">
                {{ $t('Excel') }}
              </b-dropdown-item>
            </b-dropdown>
          </b-col>
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="4"
          class="mt-1"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="glTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ data.value ? toDateWithCustomTime(data.value) : '' }}
      </template>
      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge></template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="fetchEmailReport"
      @cancel="resetFilters"
    >

      <b-form>
        <b-row class="match-height">
          <!-- Reference Number -->
          <b-col md="6">
            <InputField
              id="reference-number"
              v-model="filter.referenceNumber"
              :label="$t('Reference Number')"
              :placeholder="$t('Enter Reference Number')"
              type="text"
              for="reference-number"
            />
          </b-col>
          <!-- Reference Number -->
          <b-col md="6">
            <InputField
              id="destination-email-address"
              v-model="filter.destinationEmailAddress"
              :label="$t('Destination Email Address')"
              :placeholder="$t('Enter destination email address')"
              type="text"
              for="destination-email-address"
            />
          </b-col>
          <!-- Start Date-->
          <b-col md="6">
            <DatePickerField
              id="start-date"
              v-model="filter.startDate"
              :label="$t('Start Date')"
              :min-date="new Date()"
              :error-name="$t('Start Date')"
              :config="datePickerConfig"
              :placeholder="$t('Select Date')"
              rules="required"
            />
          </b-col>
          <!--  Start Date -->

          <!-- End Date-->
          <b-col md="6">
            <DatePickerField
              id="end-date"
              v-model="filter.endDate"
              :min-date="new Date()"
              :label="$t('End Date')"
              :error-name="$t('End Date')"
              :config="datePickerConfig"
              :placeholder="$t('Select Date')"
              rules="required"
            />
          </b-col>
          <!--  End Date -->
          <b-col md="6">
            <SelectField
              id="status"
              v-model="filter.status"
              :reduce="option => option.value"
              :label="$t('Status')"
              :placeholder="$t('Select')"
              :options="translatedStatusOptions"
              item="label"
            />
          </b-col>
        </b-row>
      </b-form>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BForm,
  BModal, BDropdown, BDropdownItem, BBadge,
} from 'bootstrap-vue'
import { required } from '@validations'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { tableMixins } from '@core/mixins/table'
import { permissionMixin } from '@core/mixins/permission'
import { modalMixins } from '@core/mixins/modal'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../forms/InputField.vue'
import Button from '../../../components/button/Button.vue'
import SelectField from '../../../forms/SelectField.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import { toDateWithCustomTime } from '../../../../helpers/formatDate'
import getStatusColor from '../../../../helpers/getStatusColor'
import { operationPermissions } from '@/helpers/permissions'

const statusOptions = [
  { label: 'Pending', value: 'Pending' },
  { label: 'Successful', value: 'Successful' },
  { label: 'Failed', value: 'Failed' },
]

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BBadge,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    TableEntries,
    InputField,
    SelectField,
    BModal,
    BDropdown,
    BDropdownItem,
    BForm,
    DatePickerField,
  },
  directives: {
    Ripple,
  },
  mixins: [sweetAlert, modalMixins, tableMixins, permissionMixin],
  data() {
    return {
      getStatusColor,
      toDateWithCustomTime,
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isDisabled: true,
      isLoading: false,
      isBusy: false,
      required,
      modalTitle: this.$t('View Email Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'destinationEmailAddress', sortable: true, label: 'Destination Email Address' },
        { key: 'referenceNumber', sortable: true, label: 'Reference Number' },
        { key: 'subject', sortable: true, label: 'Subject' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'status', label: 'Status' },
      ],
      filter: {
        referenceNumber: '',
        destinationEmailAddress: '',
        startDate: null,
        endDate: null,
        status: '',
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: '',
        defaultDate: '',
      },
      reportData: [],
      allSearchedData: [],
      selected: '',
      // statusOptions: ['Pending', 'Successful', 'Failed'],
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedStatusOptions() {
      return statusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchEmailReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchEmailReport()
    },
  },
  mounted() {
    this.fetchEmailReport()
  },
  methods: {
    ...mapActions({
      getEmailReports: 'operations/getEmailReportLists',
      downloadEmailReport: 'reports/downloadEmailReport',
    }),
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchEmailReport()
    },
    async fetchEmailReport() {
      try {
        let myStatus
        if (this.filter.status === 'Pending') {
          myStatus = 1
        } else if (this.filter.status === 'Successful') {
          myStatus = 2
        } else if (this.filter.status === 'Failed') {
          myStatus = 3
        } else {
          myStatus = null
        }
        const filter = {
          ...this.filter,
          status: myStatus,
        }
        this.hideModal('modal-filter')
        this.isBusy = true
        const response = await this.getEmailReports({
          search: filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: operationPermissions.VIEW_EMAIL_REPORT,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.isBusy = false
          this.reportData = data.data.map(item => ({
            ...item,
            // eslint-disable-next-line no-nested-ternary
            status: (item.status === 1) ? 'Pending' : (item.status === 2) ? 'Successful' : 'Failed',
          }))
          this.totalCount = data.totalItems
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alert({ text: error })
      } finally {
        this.isBusy = false
      }
    },

    async csvExport() {
      try {
        if (!this.filter.startDate || !this.filter.endDate) {
          this.alertError({ text: this.$t('Please select Start Date and End Date from filter') })
          return
        }
        this.isBusy = true
        let myStatus
        if (this.filter.status === 'Pending') {
          myStatus = 1
        } else if (this.filter.status === 'Successful') {
          myStatus = 2
        } else if (this.filter.status === 'Failed') {
          myStatus = 3
        } else {
          myStatus = null
        }
        const filter = {
          ...this.filter,
          status: myStatus,
        }
        const response = await this.downloadEmailReport({
          search: filter,
          page: this.currentPage,
          limit: this.perPage,
          fileExportFormat: 1,
          permission: operationPermissions.EXPORT_EMAIL_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        } else if (response.status === 504) {
          this.alertSuccessCustom({
            text: this.$t('Your download request is still processing, keep checking the'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('for status.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        if (!this.filter.startDate || !this.filter.endDate) {
          this.alertError({ text: this.$t('Please select Start Date and End Date from filter.') })
          return
        }
        this.isBusy = true
        let myStatus
        if (this.filter.status === 'Pending') {
          myStatus = 1
        } else if (this.filter.status === 'Successful') {
          myStatus = 2
        } else if (this.filter.status === 'Failed') {
          myStatus = 3
        } else {
          myStatus = null
        }
        const filter = {
          ...this.filter,
          status: myStatus,
        }
        const response = await this.downloadEmailReport({
          search: filter,
          page: this.currentPage,
          limit: this.perPage,
          fileExportFormat: 0,
          permission: operationPermissions.EXPORT_EMAIL_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        } else if (response.status === 504) {
          this.alertSuccessCustom({
            text: this.$t('Your download request is still processing, keep checking the'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('for status.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
    .per-page-selector {
        width: 90px;
    }

    .invoice-filter-select {
        min-width: 190px;

        ::v-deep .vs__selected-options{
            flex-wrap: nowrap;
        }

        ::v-deep .vs__selected {
            width: 100px;
        }
    }
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
